import { useState } from 'react';
import { X, Lock, Globe } from 'lucide-react';
import type { BoardVisibility } from '../../types';
import { useApp } from '../../context/AppContext';

interface BoardFormProps {
  onClose: () => void;
}

export default function BoardForm({ onClose }: BoardFormProps) {
  const { state, dispatch, apiCreateBoard } = useApp();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [teamId, setTeamId] = useState(state.teams[0]?.id || '');
  const [visibility, setVisibility] = useState<BoardVisibility>('public');
  const [allowedUserIds, setAllowedUserIds] = useState<string[]>([]);
  const [allowedTeamIds, setAllowedTeamIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || submitting) return;

    setSubmitting(true);
    const board = await apiCreateBoard({
      name: name.trim(),
      description: description.trim(),
      teamId,
      visibility,
      allowedUserIds: visibility === 'private' ? allowedUserIds : [],
      allowedTeamIds: visibility === 'private' ? allowedTeamIds : [],
      columns: [
        { id: '', title: 'Backlog', status: 'backlog', color: '#94a3b8' },
        { id: '', title: 'A Fazer', status: 'todo', color: '#3b82f6' },
        { id: '', title: 'Em Progresso', status: 'in_progress', color: '#3570E6', limit: 5 },
        { id: '', title: 'Em Revisão', status: 'in_review', color: '#f59e0b', limit: 3 },
        { id: '', title: 'Concluído', status: 'done', color: '#10b981' },
      ],
    });
    setSubmitting(false);

    if (board) {
      dispatch({ type: 'SET_BOARD', payload: board.id });
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 animate-fade-in">
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <form
        onSubmit={handleSubmit}
        className="relative bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg animate-scale-in max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-800">Novo Quadro</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="board-name" className="block text-sm font-medium text-slate-700 mb-1">
              Nome
            </label>
            <input
              id="board-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome do quadro..."
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-400 transition-all"
              autoFocus
            />
          </div>

          <div>
            <label htmlFor="board-desc" className="block text-sm font-medium text-slate-700 mb-1">
              Descrição
            </label>
            <textarea
              id="board-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrição do quadro..."
              rows={2}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-400 transition-all resize-none"
            />
          </div>

          <div>
            <label htmlFor="board-team" className="block text-sm font-medium text-slate-700 mb-1">
              Equipe
            </label>
            <select
              id="board-team"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 bg-white"
            >
              {state.teams.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Visibilidade
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setVisibility('public')}
                className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${visibility === 'public'
                    ? 'border-brand-400 bg-brand-50 text-brand-700'
                    : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
              >
                <Globe size={18} />
                <div className="text-left">
                  <p className="font-medium">Público</p>
                  <p className="text-xs text-slate-500 font-normal">Visível para todos</p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setVisibility('private')}
                className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${visibility === 'private'
                    ? 'border-brand-400 bg-brand-50 text-brand-700'
                    : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
              >
                <Lock size={18} />
                <div className="text-left">
                  <p className="font-medium">Privado</p>
                  <p className="text-xs text-slate-500 font-normal">Acesso restrito</p>
                </div>
              </button>
            </div>
          </div>

          {visibility === 'private' && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Equipes com acesso
                </label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {allowedTeamIds.map((tid) => {
                    const t = state.teams.find((tm) => tm.id === tid);
                    return t ? (
                      <span
                        key={tid}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border"
                        style={{ borderColor: `${t.color}40`, backgroundColor: `${t.color}10`, color: t.color }}
                      >
                        {t.name}
                        <button
                          type="button"
                          onClick={() => setAllowedTeamIds(allowedTeamIds.filter((id) => id !== tid))}
                          className="hover:opacity-70"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
                <select
                  onChange={(e) => {
                    if (e.target.value && !allowedTeamIds.includes(e.target.value)) {
                      setAllowedTeamIds([...allowedTeamIds, e.target.value]);
                    }
                    e.target.value = '';
                  }}
                  defaultValue=""
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-300"
                >
                  <option value="" disabled>Adicionar equipe...</option>
                  {state.teams
                    .filter((t) => !allowedTeamIds.includes(t.id))
                    .map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Usuários com acesso
                </label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {allowedUserIds.map((uid) => {
                    const u = state.users.find((usr) => usr.id === uid);
                    return u ? (
                      <span
                        key={uid}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-100 text-xs font-medium text-slate-600"
                      >
                        {u.name}
                        <button
                          type="button"
                          onClick={() => setAllowedUserIds(allowedUserIds.filter((id) => id !== uid))}
                          className="text-slate-400 hover:text-slate-600"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
                <select
                  onChange={(e) => {
                    if (e.target.value && !allowedUserIds.includes(e.target.value)) {
                      setAllowedUserIds([...allowedUserIds, e.target.value]);
                    }
                    e.target.value = '';
                  }}
                  defaultValue=""
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-300"
                >
                  <option value="" disabled>Adicionar usuário...</option>
                  {state.users
                    .filter((u) => !allowedUserIds.includes(u.id))
                    .map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                </select>
              </div>
            </>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!name.trim() || submitting}
            className="flex-1 px-4 py-2.5 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Criando...' : 'Criar Quadro'}
          </button>
        </div>
      </form>
    </div>
  );
}
